import React, { useMemo, useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import moment from 'moment/moment'

import round from 'lodash/round'
import toLower from 'lodash/toLower'

import { useDialog } from '@galo-monitor/dialog'
import { useT } from '@galo-monitor/i18n'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import { selectLanguage } from '@galo-monitor/stores/language/selectorLanguage'
import { PropertyActions } from '@galo-monitor/stores/property'
import { selectCurrentProperty, selectUnitOfMeasurement } from '@galo-monitor/stores/property/selectorProperty'
import { momentFriendlyDateFormat, momentFriendlyDateUSFormat } from '@galo-monitor/utils/dates'
import DataTable from '@galo-monitor/web-components/DataTable'
import Screen from '@galo-monitor/web-components/Screen'
import ConfirmationModal from '@galo-monitor/web-containers/modals/ConfirmationModal'
import ErrorModal from '@galo-monitor/web-containers/modals/ErrorModal'
import HarvestModal from '@galo-monitor/web-containers/modals/farm/HarvestModal'
import { useRoutes } from '@galo-monitor/web-containers/routes/authenticated'

const Harvests = () => {
  const t = useT()
  const snackbar = useSnackbar()
  const { createDialog } = useDialog()
  const dispatch = useCallback(useDispatch(), [])
  const { routes, getActionsBySlug } = useRoutes()

  const currentProperty = useSelector(selectCurrentProperty)
  const { selected } = useSelector(selectLanguage)
  const { initials } = useSelector(selectUnitOfMeasurement)

  const { data: harvests, mutate, isValidating } = useGaloMonitorApi('/farms/harvests/properties')

  const userActions = useMemo(
    () => getActionsBySlug(routes.harvests.slug),
    [getActionsBySlug, routes.harvests.slug]
  )

  const columns = useMemo(
    () => [
      {
        title: t('name'),
        field: 'name'
      },
      {
        title: t('culture', { howMany: 1 }),
        field: 'culture.name'
      },
      {
        title: t('cultivation method'),
        field: 'cultivation_method',
        render: ({ cultivationMethod }) => t(`cultivation method label ${ cultivationMethod }`)
      },
      {
        title: `${ t('total') } ${ t('area', { howMany: 1 }) } (${ initials })`,
        field: 'meta.totalplotsarea',
        render: ({ meta }) => round(meta.totalplotsarea, 2)
      },
      {
        title: t('start date'),
        field: 'date_initial',
        render: ({ dateInitial }) => moment(dateInitial)
          .format(selected === 'pt' ? momentFriendlyDateFormat : momentFriendlyDateUSFormat)
      },
      {
        title: t('end date'),
        field: 'date_finish',
        render: ({ dateFinish }) => moment(dateFinish)
          .format(selected === 'pt' ? momentFriendlyDateFormat : momentFriendlyDateUSFormat)
      },
      {
        title: t('plot', { howMany: 2 }),
        field: 'meta.totalPlots'
      }
    ],
    [initials, selected, t]
  )

  const onAddClick = useCallback(
    () => {
      createDialog({
        id: 'create-harvest',
        Component: HarvestModal,
        props: {
          onSuccess: mutate
        }
      })
    },
    [createDialog, mutate]
  )

  const onEditClick = useCallback(
    (_, harvest) => {
      createDialog({
        id: 'edit-harvest',
        Component: HarvestModal,
        props: {
          harvest,
          onSuccess: mutate
        }
      })
    },
    [createDialog, mutate]
  )

  const onDeleteClick = useCallback(
    (_, harvest) => {
      createDialog({
        id: 'confirmation',
        Component: ConfirmationModal,
        props: {
          title: t('are you sure you want to delete the "{name}" {this}?', {
            gender: 'female',
            howMany: 1,
            this: t('harvest', { howMany: 1 }),
            name: harvest.name
          }),
          onConfirm: () => {
            dispatch(PropertyActions.removeHarvest(
              harvest,
              () => {
                snackbar.success(t('{this} {action} successfully!', {
                  this: t('harvest', { howMany: 1 }),
                  action: toLower(t('removed', { gender: 'female', howMany: 1 }))
                }))
                mutate()
              },
              () => {
                createDialog({
                  id: 'harvests-error',
                  Component: ErrorModal,
                  props: {
                    title: `${ t('could not {action} the {this}', {
                      howMany: 1,
                      gender: 'male',
                      action: toLower(t('remove')),
                      this: toLower(t('harvest', { howMany: 1 }))
                    })  }.`
                  }
                })
              }
            ))
          }
        }
      })
    },
    [createDialog, dispatch, mutate, snackbar, t]
  )

  const crumbs = useMemo(() => [
    {
      title: routes.harvests.title,
      key: 'farm-harvests-screen',
      url: '/harvests',
      active: true
    }
  ], [routes.harvests.title])

  useEffect(() => {
    mutate()
  }, [currentProperty, mutate])

  return (
    <Screen title={ routes.harvests.title } breadcrumbs={ crumbs }>
      <DataTable
        loading={ isValidating }
        data={ harvests?.data || [] }
        columns={ columns }
        onAddClick={ userActions.write && onAddClick }
        onEditClick={ userActions.edit && onEditClick }
        onDeleteClick={ userActions.delete && onDeleteClick }
      />
    </Screen>
  )
}

export default Harvests
